import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDirection } from "../../../../hooks/useDirection";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Checkbox } from "../../../ui/checkbox";
import { Badge } from "../../../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../ui/tooltip";
import { Skeleton } from "../../../ui/skeleton";
import {
  Search,
  MoreVertical,
  Plus,
  Settings,
  Link as LinkIcon,
  CheckCircle2,
  Circle,
  Calendar,
  Tag,
  ArrowUpDown,
  Edit,
  Trash2,
  Flag,
  Check,
  X,
  Lightbulb,
  LayoutGrid,
  LayoutList,
  Group,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Sparkles,
  ListTodo,
  Filter,
  GripVertical,
  Globe,
  ArrowRight,
  BookOpen,
  FileText,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../ui/popover";
import CategoryManagementDialog from "./CategoryManagementDialog";
import ActionItemDialog from "./ActionItemDialog";
import NeedHelpDialog from "./NeedHelpDialog";
import BreakdownDialog from "./BreakdownDialog";
import ProgressReportDialog from "./ProgressReportDialog";
import ActionToActivityDialog from "./ActionToActivityDialog";
import ActionItemCard from "./ActionItemCard";
import MmLiftMenuItem from "../../../mentalModel/MmLiftMenuItem";
import UnifiedExtractorDialog from "../../../shared/AIExtractorDialog";
import { ExtractedItem, AIExtractedActionItem } from "../../../shared/AIExtractorDialog/types";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import { useSpaces } from "../../../../context/SpaceProvider";
import { useProjectIndex } from "../../../../hooks/useProjectIndex";
import { ProjectChip, projectColor } from "../ProjectChip";
import { projectOf } from "../../../../lib/projectIndex";
import {
  listTakeaways,
  toggleStatus,
  deleteTakeaway,
  bulkAction,
  editTakeaway,
  createTakeaway,
  reorderSubtasks,
  updateTakeawaySpaces,
  UserTakeaway,
} from "../../../../api/userTakeaways";
import { togglePwfStatus, getPwfChildren, type Pwf } from "../../../../api/pwf";
import { listCategories, createCategory, UserCategory } from "../../../../api/userCategories";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from "../../../ui/dropdown-menu";

// Sortable wrapper for subtask items (drag-and-drop reordering)
const SortableSubtaskWrapper: React.FC<{ id: string; children: React.ReactNode }> = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative" as const,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-stretch">
        <button
          className="flex items-center px-1 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-3.5 w-3.5" />
        </button>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
};

// Sortable table row for subtask reordering in table view
const SortableTableRow: React.FC<{
  id: string;
  className?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  children: (dragHandleProps: { attributes: Record<string, any>; listeners: Record<string, any> | undefined }) => React.ReactNode;
}> = ({ id, className, onMouseEnter, onMouseLeave, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children({ attributes, listeners })}
    </TableRow>
  );
};

type ViewMode = "table" | "card";
type GroupBy = "none" | "status" | "category" | "priority" | "dueDate" | "project";

interface GroupedItems {
  key: string;
  label: string;
  items: UserTakeaway[];
  color?: string;
}

interface ActionItemsProps {
  onCountChange?: (count: number) => void;
}

// Helper function to generate a varied category color
const generateCategoryColor = (): string => {
  const colors = [
    "#3B82F6", // blue
    "#8B5CF6", // violet
    "#EC4899", // pink
    "#10B981", // emerald
    "#F59E0B", // amber
    "#EF4444", // red
    "#06B6D4", // cyan
    "#6366F1", // indigo
    "#14B8A6", // teal
    "#F97316", // orange
    "#A855F7", // purple
    "#84CC16", // lime
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const ActionItems: React.FC<ActionItemsProps> = ({ onCountChange }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const { isRTL } = useDirection();
  const { spaces } = useSpaces();
  const { projectById } = useProjectIndex();
  const [items, setItems] = useState<UserTakeaway[]>([]);
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<UserTakeaway | undefined>(undefined);
  const itemDialogFromHub = useRef(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // View mode and grouping — default to card on mobile
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches
      ? "card"
      : "table"
  );
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // New dialog states for "Need help?" feature
  const [showNeedHelpDialog, setShowNeedHelpDialog] = useState(false);
  const [showBreakdownDialog, setShowBreakdownDialog] = useState(false);
  const [showActivityDialog, setShowActivityDialog] = useState(false);
  const [helpTargetItem, setHelpTargetItem] = useState<UserTakeaway | null>(null);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  // Progress report dialog state
  const [showProgressReport, setShowProgressReport] = useState(false);
  const [progressReportItem, setProgressReportItem] = useState<UserTakeaway | null>(null);

  // Track expanded parent tasks (to show/hide sub-tasks)
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  // PWF Hybrid Bridge: lazily loaded children for PWF-native parent items
  const [pwfChildren, setPwfChildren] = useState<Record<string, UserTakeaway[]>>({});

  // AI Extractor dialog
  const [showAIExtractor, setShowAIExtractor] = useState(false);

  // Mobile filter sheet
  const [showFilterSheet, setShowFilterSheet] = useState(false);

  useEffect(() => {
    fetchActionItems();
    fetchCategories();
  }, []);

  // Listen for hub quick-action to auto-open create dialog
  useEffect(() => {
    const handler = () => {
      itemDialogFromHub.current = true;
      setEditingItem(undefined);
      setShowItemDialog(true);
    };
    window.addEventListener("workspace-open-add-action", handler);
    return () => window.removeEventListener("workspace-open-add-action", handler);
  }, []);

  useEffect(() => {
    fetchActionItems();
  }, [statusFilter, priorityFilter, categoryFilter, sourceFilter]);

  const fetchCategories = async () => {
    try {
      const response = await listCategories(retryFetch, "action");
      setCategories(response.list);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchActionItems = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const filters: any = {
        type: "action" as const,
      };

      if (statusFilter !== "all") {
        filters.status = statusFilter as any;
      }
      if (priorityFilter !== "all") {
        filters.priority = priorityFilter as any;
      }
      if (categoryFilter !== "all") {
        filters.category = categoryFilter;
      }
      if (sourceFilter !== "all") {
        filters.sourceType = sourceFilter as any;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const response = await listTakeaways(retryFetch, filters);
      setItems(response.list);
      // Count only parent items (exclude sub-tasks from count)
      onCountChange?.(response.list.filter(i => i.status === "pending" && !i.parentId).length);
    } catch (error) {
      console.error("Failed to fetch action items:", error);
      if (showLoading) toast.error(t("failed_to_load_action_items"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleItemStatus = async (itemId: string) => {
    const item = items.find(i => i._id === itemId);
    if (!item) return;

    // Optimistic update — toggle immediately in UI
    const newStatus = item.status === "completed" ? "pending" : "completed";
    setItems(prev => {
      const updated = prev.map(i =>
        i._id === itemId
          ? { ...i, status: newStatus as UserTakeaway["status"], completedOn: newStatus === "completed" ? Date.now() : undefined }
          : i
      );

      // If this is a subtask, also update parent's subtaskProgress locally
      if (item.parentId) {
        const parent = updated.find(i => i._id === item.parentId);
        const prevProgress = parent?.subtaskProgress;
        const delta = newStatus === "completed" ? 1 : -1;
        const newCompleted = Math.max(0, (prevProgress?.completed ?? 0) + delta);
        const total = prevProgress?.total ?? updated.filter(i => i.parentId === item.parentId).length;
        return updated.map(i =>
          i._id === item.parentId
            ? { ...i, subtaskProgress: { total, completed: newCompleted } }
            : i
        );
      }

      return updated;
    });

    if (!item.parentId) {
      const newItems = items.map(i =>
        i._id === itemId ? { ...i, status: newStatus } : i
      );
      onCountChange?.(newItems.filter(i => i.status === "pending" && !i.parentId).length);
    }

    try {
      if (item.__isPwf) {
        await togglePwfStatus(retryFetch, itemId);
      } else {
        const response = await toggleStatus(retryFetch, { id: itemId });
        if (response.status !== "UPDATED") {
          fetchActionItems();
        }
      }
    } catch (error) {
      console.error("Failed to toggle status:", error);
      toast.error(t("failed_to_update_action_item"));
      fetchActionItems(); // Revert on error
    }
  };

  const deleteItem = async (itemId: string) => {
    try {
      const response = await deleteTakeaway(retryFetch, { id: itemId });

      if (response.status === "DELETED") {
        const newItems = items.filter(i => i._id !== itemId);
        setItems(newItems);
        toast.success(t("action_item_deleted"));
        onCountChange?.(newItems.filter(i => i.status === "pending" && !i.parentId).length);
      }
    } catch (error) {
      console.error("Failed to delete action item:", error);
      toast.error(t("failed_to_delete_action_item"));
    }
  };

  const toggleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(i => i._id)));
    }
  };

  const bulkComplete = async () => {
    try {
      const response = await bulkAction(retryFetch, {
        ids: Array.from(selectedItems),
        action: "complete",
      });

      if (response.status === "SUCCESS") {
        setItems(items.map(i =>
          selectedItems.has(i._id) ? { ...i, status: "completed" as const } : i
        ));
        setSelectedItems(new Set());
        toast.success(t("items_marked_complete"));

        // Update count (exclude sub-tasks)
        const newItems = items.map(i =>
          selectedItems.has(i._id) ? { ...i, status: "completed" as const } : i
        );
        onCountChange?.(newItems.filter(i => i.status === "pending" && !i.parentId).length);
      }
    } catch (error) {
      console.error("Failed to complete items:", error);
      toast.error(t("failed_to_update_items"));
    }
  };

  const bulkDelete = async () => {
    try {
      const response = await bulkAction(retryFetch, {
        ids: Array.from(selectedItems),
        action: "delete",
      });

      if (response.status === "SUCCESS") {
        const newItems = items.filter(i => !selectedItems.has(i._id));
        setItems(newItems);
        setSelectedItems(new Set());
        toast.success(t("items_deleted"));
        onCountChange?.(newItems.filter(i => i.status === "pending" && !i.parentId).length);
      }
    } catch (error) {
      console.error("Failed to delete items:", error);
      toast.error(t("failed_to_delete_items"));
    }
  };

  const assignCategory = async (itemId: string, categoryId: string | null) => {
    try {
      const response = await editTakeaway(retryFetch, {
        id: itemId,
        category: categoryId,
      });

      if (response.status === "UPDATED") {
        setItems(items.map(i =>
          i._id === itemId
            ? { ...i, category: categoryId ? categories.find(c => c._id === categoryId) || undefined : undefined }
            : i
        ));
        toast.success(t("category_assigned"));
      }
    } catch (error) {
      console.error("Failed to assign category:", error);
      toast.error(t("failed_to_assign_category"));
    }
  };

  const assignSpace = async (itemId: string, spaceIds: string[]) => {
    try {
      await updateTakeawaySpaces(retryFetch, itemId, spaceIds);
      setItems(items.map(i =>
        i._id === itemId ? { ...i, spaceIds } : i
      ));
      toast.success(t("space_updated") || "Space assignment updated");
    } catch (error) {
      console.error("Failed to assign space:", error);
      toast.error(t("failed_to_assign_space") || "Failed to update space");
    }
  };

  const setPriorityForItem = async (itemId: string, priority: "high" | "medium" | "low" | null) => {
    try {
      const response = await editTakeaway(retryFetch, {
        id: itemId,
        priority: priority,
      });

      if (response.status === "UPDATED") {
        setItems(items.map(i =>
          i._id === itemId ? { ...i, priority: priority || undefined } : i
        ));
        toast.success(t("priority_updated"));
      }
    } catch (error) {
      console.error("Failed to update priority:", error);
      toast.error(t("failed_to_update_priority"));
    }
  };

  const openEngagement = (item: UserTakeaway) => {
    if (item.source.fiProcess) {
      navigate(`/review?pid=${item.source.fiProcess}`);
    }
  };

  const openDesignMode = (item: UserTakeaway) => {
    if (item.source.fiPrescription) {
      // Add openSketchbook param if source is sketchbook
      const openSketchbook = item.source.type === "sketchbook" ? "&openSketchbook=true" : "";
      navigate(`/design?wid=${item.source.fiPrescription}${openSketchbook}`);
    }
  };

  const openLinkedActivity = (item: UserTakeaway) => {
    if (item.linkedActivity) {
      // If there's an existing engagement, continue it; otherwise start fresh
      if (item.linkedActivity.engagementProgress?.lastEngagementId) {
        navigate(`/review?pid=${item.linkedActivity.engagementProgress.lastEngagementId}`);
      } else {
        // Navigate to the activity prescription to start it
        navigate(`/design?wid=${item.linkedActivity.prescriptionId}`);
      }
    }
  };

  const handleAddItem = () => {
    setEditingItem(undefined);
    setShowItemDialog(true);
  };

  const handleEditItem = (item: UserTakeaway) => {
    setEditingItem(item);
    setShowItemDialog(true);
  };

  const handleSaveItem = async (data: {
    content: string;
    priority?: "high" | "medium" | "low";
    category?: string;
    dueDate?: Date;
    spaceIds?: string[];
    isLongTermGoal?: boolean;
  }) => {
    try {
      if (editingItem) {
        // Editing existing item
        const response = await editTakeaway(retryFetch, {
          id: editingItem._id,
          content: data.content,
          priority: data.priority || null,
          category: data.category || null,
          dueDate: data.dueDate ? data.dueDate.getTime() : null,
          spaceIds: data.spaceIds,
          isLongTermGoal: data.isLongTermGoal,
        });

        if (response.status === "UPDATED") {
          setItems(items.map(i =>
            i._id === editingItem._id
              ? {
                  ...i,
                  content: data.content,
                  priority: data.priority,
                  category: data.category ? categories.find(c => c._id === data.category) : undefined,
                  dueDate: data.dueDate ? data.dueDate.getTime() : undefined,
                  spaceIds: data.spaceIds,
                  isLongTermGoal: data.isLongTermGoal,
                } as any
              : i
          ));
          toast.success(t("action_item_updated"));
        }
      } else {
        // Creating new item
        const response = await createTakeaway(retryFetch, {
          type: "action",
          content: data.content,
          priority: data.priority || undefined,
          category: data.category || undefined,
          dueDate: data.dueDate ? data.dueDate.getTime() : undefined,
          spaceIds: data.spaceIds,
          isLongTermGoal: data.isLongTermGoal,
          source: {
            type: "user_created",
          },
        });

        if (response.status === "CREATED" && response.takeaway) {
          setItems([response.takeaway, ...items]);
          toast.success(t("action_item_created"));
          // New user-created items are never sub-tasks, so count is +1
          onCountChange?.(items.filter(i => i.status === "pending" && !i.parentId).length + 1);
          // Celebrate first takeaway
          window.dispatchEvent(
            new CustomEvent("clary:celebration", {
              detail: { key: "first_takeaway" },
            })
          );
        }
      }
      fetchActionItems(false); // Background refresh to get full data (no loading flash)
    } catch (error) {
      console.error("Failed to save action item:", error);
      toast.error(editingItem ? t("failed_to_update_action_item") : t("failed_to_create_action_item"));
      throw error;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case "high": return "destructive";
      case "medium": return "default";
      case "low": return "secondary";
      default: return "outline";
    }
  };

  const getSourceLabel = (source: UserTakeaway["source"]): string => {
    if (source.type === "mentor_assigned") {
      const assignedBy = source.assignedByUser?.name || source.assignedByUser?.email || t("mentor");
      return `${t("assigned_by")} ${assignedBy}`;
    }
    if (source.type === "engagement") {
      return source.engagementName || source.activityTitle || t("from_engagement");
    }
    if (source.type === "sketchbook") {
      return `Sketchbook (${source.activityTitle || t("untitled_activity")})`;
    }
    if (source.type === "reflection") {
      return t("source_reflection") || "Daily reflection";
    }
    return t("user_created");
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // "Need help?" feature handlers
  const handleNeedHelp = (item: UserTakeaway) => {
    setHelpTargetItem(item);
    setShowNeedHelpDialog(true);
  };

  const handleProgressReport = (item: UserTakeaway) => {
    setProgressReportItem(item);
    setShowProgressReport(true);
  };

  const handleBreakDown = () => {
    setShowBreakdownDialog(true);
  };

  const handleCreateActivity = () => {
    setShowActivityDialog(true);
  };

  const handleSubtasksCreated = () => {
    fetchActionItems(); // Refresh the list
  };

  const handleActivityCreated = () => {
    fetchActionItems(); // Refresh the list
  };

  // Subtask reorder handler
  const handleSubtaskReorder = useCallback(async (parentId: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const subtasks = getSubtasks(parentId);
    const oldIndex = subtasks.findIndex(s => s._id === active.id);
    const newIndex = subtasks.findIndex(s => s._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(subtasks, oldIndex, newIndex);
    const orderedIds = reordered.map(s => s._id);

    // Optimistic update: update local order
    setItems(prev => prev.map(item => {
      const idx = orderedIds.indexOf(item._id);
      if (idx !== -1) {
        return { ...item, order: idx };
      }
      return item;
    }));

    try {
      await reorderSubtasks(retryFetch, { parentId, orderedIds });
    } catch (error) {
      console.error("Failed to reorder subtasks:", error);
      toast.error(t("action_items.reorder_error", "Failed to reorder sub-tasks"));
      fetchActionItems(); // Revert on error
    }
  }, [items, retryFetch, t, fetchActionItems]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Move subtask up or down (for table view where drag-and-drop is harder)
  const handleMoveSubtask = useCallback(async (subtask: UserTakeaway, direction: "up" | "down") => {
    if (!subtask.parentId) return;
    const subtasks = getSubtasks(subtask.parentId);
    const currentIndex = subtasks.findIndex(s => s._id === subtask._id);
    if (currentIndex === -1) return;

    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= subtasks.length) return;

    const reordered = arrayMove(subtasks, currentIndex, newIndex);
    const orderedIds = reordered.map(s => s._id);

    // Optimistic update
    setItems(prev => prev.map(item => {
      const idx = orderedIds.indexOf(item._id);
      if (idx !== -1) return { ...item, order: idx };
      return item;
    }));

    try {
      await reorderSubtasks(retryFetch, { parentId: subtask.parentId, orderedIds });
    } catch (error) {
      console.error("Failed to reorder subtasks:", error);
      fetchActionItems();
    }
  }, [items, retryFetch, fetchActionItems]);

  // Direct handlers for menu actions (without "Need help?" dialog)
  const handleDirectBreakdown = (item: UserTakeaway) => {
    setHelpTargetItem(item);
    setShowBreakdownDialog(true);
  };

  const handleDirectCreateActivity = (item: UserTakeaway) => {
    setHelpTargetItem(item);
    setShowActivityDialog(true);
  };

  // AI Extractor handler
  const handleAIExtractComplete = async (extractedItems: ExtractedItem[]) => {
    // Separate insights and action items
    const actionItems = extractedItems.filter((item) => item.type === "action") as AIExtractedActionItem[];
    const insights = extractedItems.filter((item) => item.type === "insight");

    if (actionItems.length === 0 && insights.length === 0) {
      toast.info(t("ai_extractor.no_items_selected", "No items were selected"));
      return;
    }

    // Map suggested category names to IDs (or create new categories)
    const categoryNameToIdMap = new Map<string, string>();

    // Build map of existing category names to IDs
    for (const cat of categories) {
      categoryNameToIdMap.set(cat.name.toLowerCase(), cat._id);
    }

    let successCount = 0;
    let failCount = 0;

    for (const item of actionItems) {
      try {
        let categoryId: string | undefined = undefined;

        // If there's a suggested category name from AI, resolve it
        if (item.suggestedCategory) {
          // Check if suggestedCategory is already an ID (exists in categories array)
          const isAlreadyId = categories.some((cat) => cat._id === item.suggestedCategory);

          if (isAlreadyId) {
            categoryId = item.suggestedCategory;
          } else {
            // It's a name string from AI - try to find or create it
            const normalizedName = item.suggestedCategory.toLowerCase();

            if (categoryNameToIdMap.has(normalizedName)) {
              // Use existing category
              categoryId = categoryNameToIdMap.get(normalizedName);
            } else {
              // Create new category
              try {
                const newCatResponse = await createCategory(retryFetch, {
                  name: item.suggestedCategory,
                  type: "action",
                  color: generateCategoryColor(),
                });
                if (newCatResponse.status === "CREATED" && newCatResponse._id) {
                  categoryId = newCatResponse._id;
                  categoryNameToIdMap.set(normalizedName, newCatResponse._id);
                  // Update local categories list
                  if (newCatResponse.category) {
                    setCategories((prev) => [...prev, newCatResponse.category!]);
                  }
                }
              } catch (error) {
                console.error("Failed to create category:", error);
                // Continue without category
              }
            }
          }
        }

        await createTakeaway(retryFetch, {
          type: "action",
          content: item.content,
          category: categoryId,
          priority: item.suggestedPriority || undefined,
          dueDate: item.suggestedDueDate
            ? typeof item.suggestedDueDate === "number"
              ? item.suggestedDueDate
              : new Date(item.suggestedDueDate).getTime() || undefined
            : undefined,
          source: { type: "user_created" },
        });
        successCount++;
      } catch (err) {
        console.error("Failed to create action item:", err);
        failCount++;
      }
    }

    // Create insights
    let insightSuccessCount = 0;
    let insightFailCount = 0;

    for (const item of insights) {
      try {
        let categoryId: string | undefined = undefined;

        // If there's a suggested category name from AI, resolve it
        if (item.suggestedCategory) {
          // Check if suggestedCategory is already an ID (exists in categories array)
          const isAlreadyId = categories.some((cat) => cat._id === item.suggestedCategory);

          if (isAlreadyId) {
            categoryId = item.suggestedCategory;
          } else {
            // It's a name string from AI - try to find or create it
            const normalizedName = item.suggestedCategory.toLowerCase();

            if (categoryNameToIdMap.has(normalizedName)) {
              // Use existing category
              categoryId = categoryNameToIdMap.get(normalizedName);
            } else {
              // Create new category
              try {
                const newCatResponse = await createCategory(retryFetch, {
                  name: item.suggestedCategory,
                  type: "insight",
                  color: generateCategoryColor(),
                });
                if (newCatResponse.status === "CREATED" && newCatResponse._id) {
                  categoryId = newCatResponse._id;
                  categoryNameToIdMap.set(normalizedName, newCatResponse._id);
                  // Update local categories list
                  if (newCatResponse.category) {
                    setCategories((prev) => [...prev, newCatResponse.category!]);
                  }
                }
              } catch (error) {
                console.error("Failed to create category:", error);
                // Continue without category
              }
            }
          }
        }

        await createTakeaway(retryFetch, {
          type: "insight",
          content: item.content,
          category: categoryId,
          source: { type: "user_created" },
        });
        insightSuccessCount++;
      } catch (err) {
        console.error("Failed to create insight:", err);
        insightFailCount++;
      }
    }

    // Show results
    const totalSuccess = successCount + insightSuccessCount;
    const totalFail = failCount + insightFailCount;

    if (totalSuccess > 0) {
      const messages: string[] = [];
      if (successCount > 0) {
        messages.push(`${successCount} ${t("action_items", "actions")}`);
      }
      if (insightSuccessCount > 0) {
        messages.push(`${insightSuccessCount} ${t("insights", "insights")}`);
      }
      toast.success(
        t("ai_extractor.items_created", `Created ${messages.join(" and ")}`)
      );
      // Refresh both action items and categories (background, no loading flash)
      fetchActionItems(false);
      fetchCategories();
    }
    if (totalFail > 0) {
      toast.error(
        t("ai_extractor.some_items_failed", `Failed to create ${totalFail} items`)
      );
    }
  };

  const toggleParentExpanded = (parentId: string) => {
    setExpandedParents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(parentId)) {
        newSet.delete(parentId);
      } else {
        newSet.add(parentId);
        // Lazy-load PWF children on first expand
        const parent = items.find(i => i._id === parentId);
        if (parent?.__isPwf) {
          loadPwfChildren(parentId);
        }
      }
      return newSet;
    });
  };

  // Get sub-tasks for a parent item, sorted by order
  // PWF Hybrid Bridge: client-side normalization for PWF children
  const normalizePwfChild = (pwf: Pwf): UserTakeaway => ({
    _id:            pwf._id,
    __isPwf:        true,
    fiUser:         pwf.fiOwner ?? "",
    type:           "action" as const,
    format:         "text" as const,
    content:        pwf.title,
    status:         (pwf.lifecycleState === "completed" ? "completed" : pwf.lifecycleState === "archived" ? "archived" : "pending") as "pending" | "completed" | "archived",
    priority:       (pwf.config as any)?.priority,
    source:         { type: "user_created" as const },
    parentId:       pwf.pwfMeta?.fiParent ?? undefined,
    childIds:       pwf.pwfMeta?.children ?? [],
    createdOn:      pwf.createdAt ? new Date(pwf.createdAt).getTime() : 0,
    createdBy:      { _id: pwf.fiOwner ?? "", name: "", email: "" },
    updatedOn:      pwf.updatedAt ? new Date(pwf.updatedAt).getTime() : 0,
    manifestations: pwf.manifestations ?? [],
  });

  // Lazy-load PWF children on first expand
  const loadPwfChildren = async (parentId: string) => {
    if (pwfChildren[parentId]) return;
    try {
      const { children } = await getPwfChildren(retryFetch, parentId);
      setPwfChildren(prev => ({
        ...prev,
        [parentId]: (children as Pwf[]).map(normalizePwfChild),
      }));
    } catch (err) {
      console.error("Failed to load PWF children:", err);
    }
  };

  const getSubtasks = (parentId: string): UserTakeaway[] => {
    const parent = items.find(i => i._id === parentId);
    // For PWF-native parents, return lazily fetched children
    if (parent?.__isPwf) {
      return pwfChildren[parentId] ?? [];
    }
    return items
      .filter((item) => item.parentId === parentId)
      .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  };

  const toggleGroupCollapsed = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  // Group items by selected criteria
  const getGroupedItems = (items: UserTakeaway[]): GroupedItems[] => {
    if (groupBy === "none") {
      return [{ key: "all", label: "", items }];
    }

    const groups: Map<string, GroupedItems> = new Map();

    items.forEach((item) => {
      let key: string;
      let label: string;
      let color: string | undefined;

      switch (groupBy) {
        case "status":
          key = item.status || "pending";
          label = t(key);
          break;
        case "category":
          key = item.category?._id || "uncategorized";
          label = item.category?.name || t("uncategorized", "Uncategorized");
          color = item.category?.color;
          break;
        case "priority":
          key = item.priority || "no_priority";
          label = item.priority ? t(item.priority) : t("no_priority", "No Priority");
          break;
        case "dueDate":
          if (!item.dueDate) {
            key = "no_date";
            label = t("no_due_date", "No Due Date");
          } else {
            const date = new Date(item.dueDate);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            const nextWeek = new Date(today);
            nextWeek.setDate(nextWeek.getDate() + 7);

            if (date < today) {
              key = "overdue";
              label = t("overdue", "Overdue");
            } else if (date.toDateString() === today.toDateString()) {
              key = "today";
              label = t("today", "Today");
            } else if (date < tomorrow) {
              key = "tomorrow";
              label = t("tomorrow", "Tomorrow");
            } else if (date < nextWeek) {
              key = "this_week";
              label = t("this_week", "This Week");
            } else {
              key = "later";
              label = t("later", "Later");
            }
          }
          break;
        case "project": {
          const project = projectOf(projectById, item);
          key = project?.id ?? "no_project";
          label = project?.title ?? t("action_items.no_project", "No project");
          color = project ? projectColor(project, spaces) : undefined;
          break;
        }
        default:
          key = "all";
          label = "";
      }

      if (!groups.has(key)) {
        groups.set(key, { key, label, items: [], color });
      }
      groups.get(key)!.items.push(item);
    });

    // Sort groups by predefined order
    const groupOrder: Record<GroupBy, string[]> = {
      none: [],
      status: ["pending", "completed", "archived"],
      category: [], // Will be sorted alphabetically
      priority: ["high", "medium", "low", "no_priority"],
      dueDate: ["overdue", "today", "tomorrow", "this_week", "later", "no_date"],
      project: [], // Will be sorted alphabetically
    };

    const order = groupOrder[groupBy];
    const sortedGroups = Array.from(groups.values());

    if (order.length > 0) {
      sortedGroups.sort((a, b) => {
        const aIndex = order.indexOf(a.key);
        const bIndex = order.indexOf(b.key);
        if (aIndex === -1 && bIndex === -1) return a.label.localeCompare(b.label);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    } else {
      sortedGroups.sort((a, b) => a.label.localeCompare(b.label));
    }

    return sortedGroups;
  };

  // Check if an item has sub-tasks
  const hasSubtasks = (item: UserTakeaway): boolean => {
    return !!(item.childIds && item.childIds.length > 0) || !!(item.subtaskProgress && item.subtaskProgress.total > 0);
  };

  const filteredItems = items
    .filter(item => {
      // Exclude sub-tasks from main list (they'll be shown nested under parents)
      if (item.parentId) return false;
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (priorityFilter !== "all" && item.priority !== priorityFilter) return false;
      if (categoryFilter !== "all" && item.category?._id !== categoryFilter) return false;
      if (sourceFilter !== "all" && item.source.type !== sourceFilter) return false;
      if (searchQuery && !item.content.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;

      const direction = sortDirection === "asc" ? 1 : -1;

      switch (sortColumn) {
        case "content":
          return direction * a.content.localeCompare(b.content);
        case "category":
          const catA = a.category?.name || "";
          const catB = b.category?.name || "";
          return direction * catA.localeCompare(catB);
        case "priority":
          const priorityOrder = { high: 3, medium: 2, low: 1 };
          const prioA = a.priority ? priorityOrder[a.priority] : 0;
          const prioB = b.priority ? priorityOrder[b.priority] : 0;
          return direction * (prioA - prioB);
        case "source":
          const sourceA = getSourceLabel(a.source) || '';
          const sourceB = getSourceLabel(b.source) || '';
          return direction * sourceA.localeCompare(sourceB);
        case "dueDate":
          const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
          const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
          return direction * (dateA - dateB);
        default:
          return 0;
      }
    });

  // Group the filtered items
  // getGroupedItems is an unmemoized in-component function; adding it here would defeat this memo on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const groupedItems = useMemo(() => getGroupedItems(filteredItems), [filteredItems, groupBy, t, projectById, spaces]);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar - Polished header section */}
      <div className="flex flex-col gap-2 sm:gap-3 pb-3 sm:pb-4 border-b mb-3 sm:mb-4">
        {/* Row 1: Search + Action Buttons — always same row */}
        <div className="flex flex-row gap-2 items-center sm:justify-between">
          {/* Search — constrained width on desktop */}
          <div className="relative flex-1 sm:max-w-md min-w-0">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder={t("search_action_items")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-9 bg-muted/50 border-0 focus-visible:ring-1 focus-visible:ring-primary/50 transition-all"
            />
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 items-center flex-shrink-0">
            {/* Mobile filter button - shown only on mobile */}
            <Button
              size="sm"
              variant="outline"
              className="sm:hidden h-9 w-9 p-0 flex-shrink-0"
              onClick={() => setShowFilterSheet(true)}
            >
              <Filter className="h-4 w-4" />
            </Button>

            {/* Desktop filter popover — replaces inline Row 2 filters */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="hidden sm:flex h-9 px-3 flex-shrink-0 relative"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {t("filters", "Filters")}
                  {(statusFilter !== "all" || priorityFilter !== "all" || categoryFilter !== "all" || groupBy !== "none") && (
                    <span className="absolute -top-1 -end-1 w-2.5 h-2.5 bg-primary rounded-full border-2 border-background" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-0">
                <div className="p-3 pb-2 border-b">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{t("filters", "Filters")}</h4>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full">
                        {filteredItems.length} {t("action_items.items", "items")}
                      </div>
                      {(statusFilter !== "all" || priorityFilter !== "all" || categoryFilter !== "all" || groupBy !== "none") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => {
                            setStatusFilter("all");
                            setPriorityFilter("all");
                            setCategoryFilter("all");
                            setGroupBy("none" as GroupBy);
                          }}
                        >
                          {t("clear_all", "Clear")}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-3 space-y-3 max-h-80 overflow-y-auto">
                  {/* View mode */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("action_items.view", "View")}</div>
                    <div className="flex rounded-lg bg-muted p-0.5 w-fit">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2.5 rounded-md text-xs ${viewMode === "table" ? "bg-background shadow-sm" : "hover:bg-transparent"}`}
                        onClick={() => setViewMode("table")}
                      >
                        <LayoutList className="h-3.5 w-3.5 mr-1.5" />
                        {t("action_items.table_view", "Table")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 px-2.5 rounded-md text-xs ${viewMode === "card" ? "bg-background shadow-sm" : "hover:bg-transparent"}`}
                        onClick={() => setViewMode("card")}
                      >
                        <LayoutGrid className="h-3.5 w-3.5 mr-1.5" />
                        {t("action_items.card_view", "Cards")}
                      </Button>
                    </div>
                  </div>

                  {/* Group by */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("action_items.group_by", "Group by")}</div>
                    <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue placeholder={t("action_items.group_by", "Group by")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">{t("action_items.no_grouping", "No grouping")}</SelectItem>
                        <SelectItem value="status">{t("action_items.group_by_status", "By status")}</SelectItem>
                        <SelectItem value="category">{t("action_items.group_by_category", "By category")}</SelectItem>
                        <SelectItem value="priority">{t("action_items.group_by_priority", "By priority")}</SelectItem>
                        <SelectItem value="dueDate">{t("action_items.group_by_due_date", "By due date")}</SelectItem>
                        <SelectItem value="project">{t("action_items.group_by_project", "By project")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("status")}</div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue placeholder={t("status")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_statuses")}</SelectItem>
                        <SelectItem value="pending">{t("pending")}</SelectItem>
                        <SelectItem value="completed">{t("completed")}</SelectItem>
                        <SelectItem value="archived">{t("archived")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Priority */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("priority")}</div>
                    <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue placeholder={t("priority")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("all_priorities")}</SelectItem>
                        <SelectItem value="high">{t("high")}</SelectItem>
                        <SelectItem value="medium">{t("medium")}</SelectItem>
                        <SelectItem value="low">{t("low")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category */}
                  <div className="space-y-1.5">
                    <div className="text-xs font-medium text-muted-foreground">{t("action_items.all_categories", "Category")}</div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-full h-8 text-xs">
                        <SelectValue placeholder={t("action_items.all_categories", "All categories")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t("action_items.all_categories", "All categories")}</SelectItem>
                        {categories.map((cat) => (
                          <SelectItem key={cat._id} value={cat._id}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Manage categories */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full justify-start text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => setShowCategoryDialog(true)}
                  >
                    <Settings className="h-3.5 w-3.5 mr-1.5" />
                    {t("manage_categories")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>

            <Button
              size="sm"
              variant="outline"
              className="h-9 w-9 p-0 sm:w-auto sm:px-4 bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200 text-violet-700 hover:from-violet-100 hover:to-purple-100 hover:border-violet-300 flex-shrink-0"
              onClick={() => setShowAIExtractor(true)}
            >
              <Sparkles className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t("ai_extractor.extract", "AI Extract")}</span>
            </Button>
            <Button size="sm" className="h-9 w-9 p-0 sm:w-auto sm:px-4 shadow-sm flex-shrink-0" onClick={handleAddItem}>
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t("add_action_item")}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Bulk Actions - Animated slide-in */}
      <div className={`overflow-hidden transition-all duration-200 ease-out ${selectedItems.size > 0 ? "max-h-16 opacity-100 mb-4" : "max-h-0 opacity-0"}`}>
        <div className="flex items-center gap-3 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{t("items_selected", {count: selectedItems.size})}</span>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" className="h-8" onClick={bulkComplete}>
              <Check className="h-3.5 w-3.5 mr-1.5" />
              {t("mark_complete")}
            </Button>
            <Button size="sm" variant="outline" className="h-8 text-destructive hover:text-destructive" onClick={bulkDelete}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              {t("delete")}
            </Button>
            <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelectedItems(new Set())}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(categoryFilter !== "all" || statusFilter !== "all" || priorityFilter !== "all") && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-muted-foreground">{t("active_filters", "Active filters")}:</span>
          {categoryFilter !== "all" && (() => {
            const cat = categories.find(c => c._id === categoryFilter);
            return cat ? (
              <span
                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium border"
                style={{ backgroundColor: `${cat.color}18`, borderColor: `${cat.color}40`, color: cat.color }}
              >
                <Tag className="h-3 w-3" />
                {cat.name}
                <button
                  className="ms-0.5 hover:opacity-70 transition-opacity"
                  onClick={() => setCategoryFilter("all")}
                  aria-label={t("clear_filter", "Clear filter")}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ) : null;
          })()}
          {statusFilter !== "all" && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-primary/5 border border-primary/20 text-primary">
              {t(statusFilter)}
              <button
                className="ms-0.5 hover:opacity-70 transition-opacity"
                onClick={() => setStatusFilter("all")}
                aria-label={t("clear_filter", "Clear filter")}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {priorityFilter !== "all" && (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium bg-orange-50 border border-orange-200 text-orange-700">
              <Flag className="h-3 w-3" />
              {t(priorityFilter)}
              <button
                className="ms-0.5 hover:opacity-70 transition-opacity"
                onClick={() => setPriorityFilter("all")}
                aria-label={t("clear_filter", "Clear filter")}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <button
            className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            onClick={() => { setCategoryFilter("all"); setStatusFilter("all"); setPriorityFilter("all"); }}
          >
            {t("clear_all", "Clear all")}
          </button>
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden rounded-xl">
        {loading ? (
          <div className="space-y-4 p-4">
            {/* List skeleton */}
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-4 flex-1 max-w-md" />
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-4 w-20 hidden md:block" />
              </div>
            ))}
          </div>
        ) : filteredItems.length === 0 && (searchQuery || statusFilter !== "all" || priorityFilter !== "all" || categoryFilter !== "all" || sourceFilter !== "all") ? (
          /* Filtered empty state */
          <div className="flex flex-col items-center justify-center text-center p-6 min-h-[300px]">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
              <Filter className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1.5">
              {t("no_matching_action_items")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-5">
              {t("no_action_items_match_filters")}
            </p>
            <Button variant="outline" size="sm" onClick={() => {
              setSearchQuery("");
              setStatusFilter("all");
              setPriorityFilter("all");
              setCategoryFilter("all");
              setSourceFilter("all");
            }}>
              <Filter className="h-3.5 w-3.5 me-2" />
              {t("clear_filters") || "Clear Filters"}
            </Button>
          </div>
        ) : filteredItems.length === 0 ? (
          /* Onboarding empty state */
          <div className="flex flex-col items-center justify-center text-center p-6 min-h-[400px]">
            {/* Hero Icon */}
            <div className="relative mb-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center shadow-sm">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
              </div>
              <div className="absolute -top-1 -end-1 w-5 h-5 rounded-full bg-violet-100 flex items-center justify-center">
                <Sparkles className="h-2.5 w-2.5 text-violet-500" />
              </div>
              <div className="absolute -bottom-1 -start-1 w-4 h-4 rounded-full bg-green-200 flex items-center justify-center">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
              </div>
            </div>

            {/* Title & Description */}
            <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
              {t("action_items_empty_title")}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-6 leading-relaxed">
              {t("action_items_help_text")}
            </p>

            {/* Action Cards */}
            <div className="grid gap-2.5 w-full max-w-md">
              {/* Add Manually */}
              <button
                onClick={handleAddItem}
                className="flex items-center gap-3 p-3 rounded-xl border border-green-100 bg-gradient-to-r rtl:bg-gradient-to-l from-green-50/50 to-white hover:from-green-50 hover:to-green-50/50 hover:border-green-200 transition-all text-start group"
              >
                <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                  <Plus className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("add_first_action_item")}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t("add_first_action_item_description")}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-green-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
              </button>

              {/* AI Extract */}
              <button
                onClick={() => setShowAIExtractor(true)}
                className="flex items-center gap-3 p-3 rounded-xl border border-violet-100 bg-gradient-to-r rtl:bg-gradient-to-l from-violet-50/50 to-white hover:from-violet-50 hover:to-violet-50/50 hover:border-violet-200 transition-all text-start group"
              >
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 group-hover:bg-violet-200 transition-colors">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("extract_action_items_with_ai")}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t("extract_action_items_with_ai_description")}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-violet-500 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
              </button>

              {/* Run Engagement */}
              <button
                onClick={() => navigate("/portal")}
                className="flex items-center gap-3 p-3 rounded-xl border border-primary/10 bg-gradient-to-r rtl:bg-gradient-to-l from-primary/5/50 to-white hover:from-primary/5 hover:to-primary/5/50 hover:border-primary/20 transition-all text-start group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <BookOpen className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-medium text-gray-900">
                    {t("run_engagement_to_capture_actions")}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t("action_items_from_engagements_description")}
                  </p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all" />
              </button>
            </div>

            {/* Tips Section */}
            <div className="mt-6 pt-4 border-t border-gray-100 w-full max-w-md">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-gray-600">{t("tip")}:</span>{" "}
                {t("action_items_tip")}
              </p>
            </div>
          </div>
        ) : viewMode === "card" ? (
          /* Card View */
          <div className="overflow-auto h-full space-y-8 pb-4 pr-1">
            {groupedItems.map((group) => (
              <div key={group.key}>
                {/* Group header (only if grouping is enabled) */}
                {groupBy !== "none" && (
                  <button
                    className="flex items-center gap-2.5 mb-4 w-full text-start group"
                    onClick={() => toggleGroupCollapsed(group.key)}
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-md bg-muted/50 group-hover:bg-muted transition-colors">
                      {collapsedGroups.has(group.key) ? (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    {group.color && (
                      <div className="w-3 h-3 rounded-full ring-2 ring-offset-2 ring-offset-background" style={{ backgroundColor: group.color }} />
                    )}
                    <span className="font-semibold text-sm">{group.label}</span>
                    <Badge variant="secondary" className="ms-1 h-5 px-2 text-xs font-normal">
                      {group.items.length}
                    </Badge>
                    <div className="flex-1 h-px bg-border ms-2" />
                  </button>
                )}

                {/* Group items */}
                <div className={`transition-all duration-200 ${collapsedGroups.has(group.key) ? "max-h-0 overflow-hidden opacity-0" : "max-h-[5000px] opacity-100"}`}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {group.items.map((item) => (
                      <div key={item._id} className="flex flex-col">
                        {/* Parent card */}
                        <div className="relative group/card">
                          <ActionItemCard
                            item={item}
                            isSelected={selectedItems.has(item._id)}
                            categories={categories}
                            onToggleSelect={toggleSelectItem}
                            onToggleStatus={toggleItemStatus}
                            onEdit={handleEditItem}
                            onDelete={deleteItem}
                            onSetPriority={setPriorityForItem}
                            onAssignCategory={assignCategory}
                            onAssignSpace={assignSpace}
                            onNeedHelp={handleNeedHelp}
                            onAddSubtask={handleDirectBreakdown}
                            onOpenEngagement={openEngagement}
                            onOpenDesignMode={openDesignMode}
                            onOpenLinkedActivity={openLinkedActivity}
                            onProgressReport={handleProgressReport}
                            getSourceLabel={getSourceLabel}
                          />
                          {/* Expand sub-tasks button */}
                          {hasSubtasks(item) && (
                            <button
                              className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-background border shadow-sm text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-all z-10"
                              onClick={() => toggleParentExpanded(item._id)}
                            >
                              {expandedParents.has(item._id) ? (
                                <>
                                  <ChevronDown className="h-3 w-3" />
                                  <span>Hide</span>
                                </>
                              ) : (
                                <>
                                  <ListTodo className="h-3 w-3" />
                                  <span>{item.subtaskProgress?.total || getSubtasks(item._id).length}</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>

                        {/* Sub-tasks when expanded */}
                        <div className={`transition-all duration-200 ease-out ${expandedParents.has(item._id) && getSubtasks(item._id).length > 0 ? "max-h-[1000px] opacity-100 mt-4" : "max-h-0 opacity-0 overflow-hidden"}`}>
                          <div className="ms-3 ps-3 border-s-2 border-primary/20 space-y-2">
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              modifiers={[restrictToVerticalAxis]}
                              onDragEnd={(event) => handleSubtaskReorder(item._id, event)}
                            >
                              <SortableContext
                                items={getSubtasks(item._id).map(s => s._id)}
                                strategy={verticalListSortingStrategy}
                              >
                            {getSubtasks(item._id).map((subtask) => (
                              <SortableSubtaskWrapper key={subtask._id} id={subtask._id}>
                              <div
                                className={`group/subtask p-3 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors ${subtask.status === "completed" ? "opacity-50" : ""}`}
                              >
                                <div className="flex items-start gap-2.5">
                                  <Checkbox
                                    checked={selectedItems.has(subtask._id)}
                                    onCheckedChange={() => toggleSelectItem(subtask._id)}
                                    className="mt-0.5"
                                  />
                                  <button
                                    className={`group/toggle flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-full transition-colors ${subtask.status === "completed" ? "bg-green-500 text-white" : "bg-muted hover:bg-primary/20 text-muted-foreground"}`}
                                    onClick={() => toggleItemStatus(subtask._id)}
                                  >
                                    <Check className={`h-3 w-3 transition-opacity ${subtask.status === "completed" ? "opacity-100" : "opacity-0 group-hover/toggle:opacity-50"}`} />
                                  </button>
                                  <span className={`flex-1 text-sm leading-snug ${subtask.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                                    {subtask.content}
                                  </span>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 opacity-0 group-hover/subtask:opacity-100 transition-opacity">
                                        <MoreVertical className="h-3 w-3" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => handleEditItem(subtask)}>
                                        <Edit className="h-4 w-4 mr-2" />
                                        {t("edit")}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => toggleItemStatus(subtask._id)}>
                                        {subtask.status === "completed" ? (
                                          <>
                                            <X className="h-4 w-4 mr-2" />
                                            {t("mark_incomplete")}
                                          </>
                                        ) : (
                                          <>
                                            <Check className="h-4 w-4 mr-2" />
                                            {t("mark_complete")}
                                          </>
                                        )}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleDirectBreakdown(subtask)}>
                                        <ListTodo className="h-4 w-4 mr-2 text-primary" />
                                        {t("action_items.add_subtasks", "Add Sub-tasks")}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => deleteItem(subtask._id)}
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        {t("delete")}
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                                {/* Sub-task meta info */}
                                {(subtask.priority || subtask.dueDate) && (
                                  <div className="flex flex-wrap items-center gap-1.5 mt-2 ms-11">
                                    {subtask.priority && (
                                      <Badge variant={getPriorityColor(subtask.priority)} className="h-5 text-[10px] px-1.5">
                                        {t(subtask.priority)}
                                      </Badge>
                                    )}
                                    {subtask.dueDate && (
                                      <Badge variant="outline" className={`h-5 text-[10px] px-1.5 ${new Date(subtask.dueDate) < new Date() && subtask.status !== "completed" ? "border-red-500 text-red-600" : ""}`}>
                                        <Calendar className="h-2.5 w-2.5 mr-1" />
                                        {new Date(subtask.dueDate).toLocaleDateString()}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </div>
                              </SortableSubtaskWrapper>
                            ))}
                              </SortableContext>
                            </DndContext>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="border rounded-xl overflow-hidden bg-card h-full">
            <div className="overflow-auto h-full">
              {groupedItems.map((group, groupIndex) => (
                <div key={group.key}>
                  {/* Group header for table (only if grouping is enabled) */}
                  {groupBy !== "none" && (
                    <button
                      className="flex items-center gap-2.5 w-full text-start bg-muted/40 hover:bg-muted/60 px-4 py-2.5 border-b transition-colors group"
                      onClick={() => toggleGroupCollapsed(group.key)}
                    >
                      <div className="flex items-center justify-center w-5 h-5 rounded bg-background/50 group-hover:bg-background transition-colors">
                        {collapsedGroups.has(group.key) ? (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </div>
                      {group.color && (
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: group.color }} />
                      )}
                      <span className="font-medium text-sm">{group.label}</span>
                      <Badge variant="secondary" className="ms-1 h-5 px-2 text-xs font-normal">
                        {group.items.length}
                      </Badge>
                    </button>
                  )}

                  <div className={`transition-all duration-200 ${collapsedGroups.has(group.key) ? "max-h-0 overflow-hidden" : ""}`}>
                    <Table>
                      {/* Only show header for first group or when no grouping */}
                      {(groupIndex === 0 || groupBy === "none") && (
                        <TableHeader className="sticky top-0 bg-muted/30 backdrop-blur-sm z-10">
                          <TableRow className="hover:bg-transparent border-b">
                            <TableHead className="w-10 sm:w-14 pl-2 sm:pl-4">
                              <Checkbox
                                checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
                                onCheckedChange={toggleSelectAll}
                              />
                            </TableHead>
                            <TableHead className="hidden sm:table-cell w-10"></TableHead>
                            <TableHead>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
                                onClick={() => handleSort("content")}
                              >
                                {t("content")}
                                <ArrowUpDown className="ml-1.5 h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="hidden md:table-cell w-[130px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
                                onClick={() => handleSort("category")}
                              >
                                {t("category")}
                                <ArrowUpDown className="ml-1.5 h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="hidden md:table-cell w-[100px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
                                onClick={() => handleSort("priority")}
                              >
                                {t("priority")}
                                <ArrowUpDown className="ml-1.5 h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="hidden lg:table-cell w-[150px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
                                onClick={() => handleSort("source")}
                              >
                                {t("source")}
                                <ArrowUpDown className="ml-1.5 h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="hidden lg:table-cell w-[110px]">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="-ml-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
                                onClick={() => handleSort("dueDate")}
                              >
                                {t("due_date")}
                                <ArrowUpDown className="ml-1.5 h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="w-10 sm:w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                      )}
                      <TableBody>
                        {group.items.map((item) => (
                          <React.Fragment key={item._id}>
                            <TableRow
                              className={`group/row transition-all duration-150 border-b border-transparent hover:border-border ${item.status === "completed" ? "opacity-50 bg-muted/20" : "hover:bg-muted/40"}`}
                              onMouseEnter={() => setHoveredRowId(item._id)}
                              onMouseLeave={() => setHoveredRowId(null)}
                            >
                              <TableCell className="pl-2 sm:pl-4">
                                <div className="flex items-center gap-1">
                                  {hasSubtasks(item) ? (
                                    <button
                                      className="hidden sm:flex items-center justify-center w-5 h-5 rounded hover:bg-muted transition-colors"
                                      onClick={() => toggleParentExpanded(item._id)}
                                    >
                                      {expandedParents.has(item._id) ? (
                                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                                      )}
                                    </button>
                                  ) : (
                                    <div className="hidden sm:block w-5" /> /* Spacer to maintain alignment */
                                  )}
                                  <Checkbox
                                    checked={selectedItems.has(item._id)}
                                    onCheckedChange={() => toggleSelectItem(item._id)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <button
                                  className={`group/toggle flex items-center justify-center w-6 h-6 rounded-full transition-colors ${item.status === "completed" ? "bg-green-500 text-white" : "bg-muted hover:bg-primary/20 text-muted-foreground"}`}
                                  onClick={() => toggleItemStatus(item._id)}
                                >
                                  <Check className={`h-3.5 w-3.5 transition-opacity ${item.status === "completed" ? "opacity-100" : "opacity-0 group-hover/toggle:opacity-50"}`} />
                                </button>
                              </TableCell>
                              <TableCell className={`py-3 ${item.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                                <div className="space-y-1.5">
                                  {/* Content text — full width, no squeezing */}
                                  <p className="text-sm leading-relaxed">{item.content}</p>
                                  {/* Visual indicators row — below text, wrapping */}
                                  <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                                    {item.isLongTermGoal && (
                                      <Badge variant="outline" className="h-5 text-[10px] font-normal px-1.5 border-teal-300 text-teal-600 bg-teal-50 dark:bg-teal-900/20">
                                        <Target className="h-2.5 w-2.5 mr-1" />
                                        {t("long_term_goal", "Long-term goal")}
                                      </Badge>
                                    )}
                                    <ProjectChip item={item} projectById={projectById} spaces={spaces} variant="badge" />
                                    {item.spaceIds && item.spaceIds.length > 0 && spaces && spaces.map((space) => {
                                      if (!item.spaceIds?.includes(space._id)) return null;
                                      return (
                                        <Badge
                                          key={space._id}
                                          variant="outline"
                                          className="h-5 text-[10px] font-normal px-1.5 cursor-pointer hover:opacity-80 transition-opacity"
                                          style={{ borderColor: space.color + "50", color: space.color }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            window.dispatchEvent(new CustomEvent("spaces-select-space", { detail: space._id }));
                                            navigate("/spaces", { state: { selectedSpaceId: space._id } });
                                          }}
                                        >
                                          <Globe className="w-2.5 h-2.5 mr-1" />
                                          {space.name}
                                        </Badge>
                                      );
                                    })}
                                    {item.subtaskProgress && (
                                      <TooltipProvider delayDuration={300}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge variant="secondary" className="h-5 text-[10px] px-1.5 font-normal">
                                              <ListTodo className="h-3 w-3 mr-1" />
                                              {item.subtaskProgress.completed}/{item.subtaskProgress.total}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p>{t("action_items.subtasks_progress", "Sub-tasks: {{completed}} of {{total}}", item.subtaskProgress)}</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {(item.progressReportCount ?? 0) > 0 && (
                                      <Badge
                                        variant="outline"
                                        className="h-5 text-[10px] px-1.5 font-normal border-teal-300 text-teal-600 bg-teal-50 dark:bg-teal-900/20 cursor-pointer hover:bg-teal-100"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleProgressReport(item);
                                        }}
                                      >
                                        <FileText className="h-3 w-3 mr-1" />
                                        {item.progressReportCount}
                                      </Badge>
                                    )}
                                    {item.linkedActivity && (
                                      <TooltipProvider delayDuration={300}>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <Badge
                                              variant="secondary"
                                              className="h-5 text-[10px] px-1.5 text-purple-600 border-purple-200 bg-purple-50 dark:bg-purple-900/20 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openLinkedActivity(item);
                                              }}
                                            >
                                              <Sparkles className="h-3 w-3 mr-1" />
                                              {item.linkedActivity.engagementProgress ? (
                                                <span>{item.linkedActivity.engagementProgress.completedPanes}/{item.linkedActivity.engagementProgress.totalPanes}</span>
                                              ) : null}
                                            </Badge>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <div className="text-xs">
                                              <p className="font-medium">{item.linkedActivity.prescriptionName}</p>
                                              {item.linkedActivity.engagementProgress && (
                                                <p className="text-muted-foreground mt-1">
                                                  {item.linkedActivity.engagementProgress.started
                                                    ? t("action_items.activity_progress", "Progress: {{completed}}/{{total}}", {
                                                        completed: item.linkedActivity.engagementProgress.completedPanes,
                                                        total: item.linkedActivity.engagementProgress.totalPanes
                                                      })
                                                    : t("action_items.activity_not_started", "Not started yet")}
                                                </p>
                                              )}
                                              <p className="text-muted-foreground mt-1">{t("action_items.click_to_open", "Click to open")}</p>
                                            </div>
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {/* "Need help?" button - shows on hover */}
                                    {item.status !== "completed" && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className={`h-6 px-2 text-amber-600 hover:text-amber-700 bg-amber-50/80 hover:bg-amber-100 border border-amber-200/50 hover:border-amber-300 rounded-full flex-shrink-0 transition-all duration-200 ${hoveredRowId === item._id ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2 pointer-events-none"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNeedHelp(item);
                                        }}
                                      >
                                        <Lightbulb className="h-3.5 w-3.5 mr-1" />
                                        <span className="text-xs font-medium">{t("action_items.need_help", "Need help?")}</span>
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {item.category ? (
                                <Badge
                                  variant="outline"
                                  className="font-normal text-xs cursor-pointer hover:opacity-80 transition-opacity"
                                  style={{ borderColor: item.category.color, color: item.category.color }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCategoryFilter(item.category!._id);
                                  }}
                                >
                                  {item.category.name}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground/50">—</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell">
                              {item.priority ? (
                                <Badge variant={getPriorityColor(item.priority)} className="text-xs font-normal">
                                  {t(item.priority)}
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground/50">—</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              <span className="text-xs text-muted-foreground truncate max-w-[140px] block">
                                {getSourceLabel(item.source)}
                              </span>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {item.dueDate ? (
                                <div className={`flex items-center gap-1.5 text-xs ${new Date(item.dueDate) < new Date() && item.status !== "completed" ? "text-red-600 font-medium" : "text-muted-foreground"}`}>
                                  <Calendar className="h-3 w-3" />
                                  {new Date(item.dueDate).toLocaleDateString()}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground/50">—</span>
                              )}
                            </TableCell>
                            <TableCell className="pr-2 sm:pr-4">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 sm:opacity-0 sm:group-hover/row:opacity-100 transition-opacity">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => toggleItemStatus(item._id)}>
                                    {item.status === "completed" ? (
                                      <>
                                        <Circle className="h-4 w-4 mr-2" />
                                        {t("mark_incomplete")}
                                      </>
                                    ) : (
                                      <>
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        {t("mark_complete")}
                                      </>
                                    )}
                                  </DropdownMenuItem>

                                  <DropdownMenuSeparator />

                                  <DropdownMenuItem onClick={() => handleEditItem(item)}>
                                    <Edit className="h-4 w-4 mr-2" />
                                    {t("edit")}
                                  </DropdownMenuItem>

                                  <MmLiftMenuItem entityId={item._id} />

                                  {item.status !== "completed" && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleNeedHelp(item)} className="sm:hidden">
                                        <Lightbulb className="h-4 w-4 mr-2 text-amber-600" />
                                        {t("action_items.need_help", "Need help?")}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDirectBreakdown(item)}>
                                        <ListTodo className="h-4 w-4 mr-2 text-primary" />
                                        {t("action_items.add_subtasks", "Add Sub-tasks")}
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDirectCreateActivity(item)}>
                                        <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
                                        {t("action_items.create_activity", "Create Activity")}
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}

                                  <DropdownMenuItem onClick={() => handleProgressReport(item)}>
                                    <FileText className="h-4 w-4 mr-2 text-teal-500" />
                                    {t("progress_report", "Progress Update")}
                                  </DropdownMenuItem>

                                  <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                      <Flag className="h-4 w-4 mr-2" />
                                      {t("set_priority")}
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                      <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => setPriorityForItem(item._id, null)}>
                                          <X className="h-4 w-4 mr-2 text-muted-foreground" />
                                          {t("no_priority")}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={() => setPriorityForItem(item._id, "high")}>
                                          <Flag className="h-4 w-4 mr-2 text-red-500" />
                                          {t("high")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setPriorityForItem(item._id, "medium")}>
                                          <Flag className="h-4 w-4 mr-2 text-orange-500" />
                                          {t("medium")}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setPriorityForItem(item._id, "low")}>
                                          <Flag className="h-4 w-4 mr-2 text-primary" />
                                          {t("low")}
                                        </DropdownMenuItem>
                                      </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                  </DropdownMenuSub>

                                  <DropdownMenuSub>
                                    <DropdownMenuSubTrigger>
                                      <Tag className="h-4 w-4 mr-2" />
                                      {t("assign_category")}
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                      <DropdownMenuSubContent>
                                        <DropdownMenuItem onClick={() => assignCategory(item._id, null)}>
                                          <X className="h-4 w-4 mr-2 text-muted-foreground" />
                                          {t("no_category")}
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        {categories.map((category) => (
                                          <DropdownMenuItem
                                            key={category._id}
                                            onClick={() => assignCategory(item._id, category._id)}
                                          >
                                            <div className="flex items-center gap-2">
                                              <div
                                                className="w-3 h-3 rounded-full border"
                                                style={{ backgroundColor: category.color }}
                                              />
                                              {category.name}
                                            </div>
                                          </DropdownMenuItem>
                                        ))}
                                      </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                  </DropdownMenuSub>

                                  {spaces && spaces.length > 0 && (
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger>
                                        <Globe className="h-4 w-4 mr-2" />
                                        {t("assign_to_space") || "Assign to Space"}
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuPortal>
                                        <DropdownMenuSubContent>
                                          {spaces.map((space) => {
                                            const isAssigned = item.spaceIds?.includes(space._id);
                                            return (
                                              <DropdownMenuItem
                                                key={space._id}
                                                onClick={() => {
                                                  const currentIds = item.spaceIds || [];
                                                  const newIds = isAssigned
                                                    ? currentIds.filter((id) => id !== space._id)
                                                    : [...currentIds, space._id];
                                                  assignSpace(item._id, newIds);
                                                }}
                                              >
                                                <div className="flex items-center gap-2 w-full">
                                                  <div
                                                    className="w-3 h-3 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: space.color }}
                                                  />
                                                  <span className="flex-1 truncate">
                                                    {space.name}
                                                  </span>
                                                  {isAssigned && (
                                                    <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                                                  )}
                                                </div>
                                              </DropdownMenuItem>
                                            );
                                          })}
                                        </DropdownMenuSubContent>
                                      </DropdownMenuPortal>
                                    </DropdownMenuSub>
                                  )}

                                  {(item.source.fiProcess || item.source.fiPrescription) && (
                                    <>
                                      <DropdownMenuSeparator />
                                      {item.source.type === "engagement" && (
                                        <DropdownMenuItem onClick={() => openEngagement(item)}>
                                          <LinkIcon className="h-4 w-4 mr-2" />
                                          {t("view_engagement")}
                                        </DropdownMenuItem>
                                      )}
                                      {item.source.type === "sketchbook" && (
                                        <DropdownMenuItem onClick={() => openDesignMode(item)}>
                                          <LinkIcon className="h-4 w-4 mr-2" />
                                          {t("open_in_design_mode")}
                                        </DropdownMenuItem>
                                      )}
                                    </>
                                  )}

                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => deleteItem(item._id)}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {t("delete")}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>

                          {/* Sub-task rows - shown when parent is expanded */}
                          {expandedParents.has(item._id) && (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            modifiers={[restrictToVerticalAxis]}
                            onDragEnd={(event) => handleSubtaskReorder(item._id, event)}
                          >
                            <SortableContext
                              items={getSubtasks(item._id).map(s => s._id)}
                              strategy={verticalListSortingStrategy}
                            >
                          {getSubtasks(item._id).map((subtask, subtaskIndex) => (
                            <SortableTableRow
                              key={subtask._id}
                              id={subtask._id}
                              className={`group/subtask transition-all duration-150 bg-muted/10 border-b border-transparent ${subtask.status === "completed" ? "opacity-40" : "hover:bg-muted/30"}`}
                              onMouseEnter={() => setHoveredRowId(subtask._id)}
                              onMouseLeave={() => setHoveredRowId(null)}
                            >
                              {(dragHandleProps) => (
                              <>
                              <TableCell className="pl-2 sm:pl-4">
                                <div className="flex items-center gap-1">
                                  <button
                                    className="hidden sm:flex w-5 justify-center cursor-grab active:cursor-grabbing text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors touch-none"
                                    {...dragHandleProps.attributes}
                                    {...dragHandleProps.listeners}
                                  >
                                    <GripVertical className="h-3.5 w-3.5" />
                                  </button>
                                  <Checkbox
                                    checked={selectedItems.has(subtask._id)}
                                    onCheckedChange={() => toggleSelectItem(subtask._id)}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <button
                                  className={`group/toggle flex items-center justify-center w-5 h-5 rounded-full transition-colors ${subtask.status === "completed" ? "bg-green-500 text-white" : "bg-muted hover:bg-primary/20 text-muted-foreground"}`}
                                  onClick={() => toggleItemStatus(subtask._id)}
                                >
                                  <Check className={`h-3 w-3 transition-opacity ${subtask.status === "completed" ? "opacity-100" : "opacity-0 group-hover/toggle:opacity-50"}`} />
                                </button>
                              </TableCell>
                              <TableCell className={`py-2.5 ${subtask.status === "completed" ? "line-through text-muted-foreground" : ""}`}>
                                <div className="flex items-center gap-2 ps-2 border-s-2 border-primary/20">
                                  <span className="text-sm text-muted-foreground">{subtask.content}</span>
                                </div>
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                {subtask.category ? (
                                  <Badge variant="outline" className="font-normal text-[10px]" style={{ borderColor: subtask.category.color, color: subtask.category.color }}>
                                    {subtask.category.name}
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground/50">—</span>
                                )}
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                {subtask.priority ? (
                                  <Badge variant={getPriorityColor(subtask.priority)} className="text-[10px] font-normal">
                                    {t(subtask.priority)}
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground/50">—</span>
                                )}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider">
                                  {t("action_items.subtask", "Sub-task")}
                                </span>
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                {subtask.dueDate ? (
                                  <div className={`flex items-center gap-1.5 text-xs ${new Date(subtask.dueDate) < new Date() && subtask.status !== "completed" ? "text-red-600" : "text-muted-foreground"}`}>
                                    <Calendar className="h-3 w-3" />
                                    {new Date(subtask.dueDate).toLocaleDateString()}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground/50">—</span>
                                )}
                              </TableCell>
                              <TableCell className="pr-2 sm:pr-4">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 sm:opacity-0 sm:group-hover/subtask:opacity-100 transition-opacity">
                                      <MoreVertical className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => toggleItemStatus(subtask._id)}>
                                      {subtask.status === "completed" ? (
                                        <>
                                          <Circle className="h-4 w-4 mr-2" />
                                          {t("mark_incomplete")}
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle2 className="h-4 w-4 mr-2" />
                                          {t("mark_complete")}
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleEditItem(subtask)}>
                                      <Edit className="h-4 w-4 mr-2" />
                                      {t("edit")}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDirectBreakdown(subtask)}>
                                      <ListTodo className="h-4 w-4 mr-2 text-primary" />
                                      {t("action_items.add_subtasks", "Add Sub-tasks")}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      className="text-destructive"
                                      onClick={() => deleteItem(subtask._id)}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      {t("delete")}
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                              </>
                              )}
                            </SortableTableRow>
                          ))}
                            </SortableContext>
                          </DndContext>
                          )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <CategoryManagementDialog
        open={showCategoryDialog}
        onOpenChange={(open) => {
          setShowCategoryDialog(open);
          if (!open) {
            fetchActionItems();
          }
        }}
        type="action"
        onCategoryCreated={(newCat) => {
          setCategories((prev) => [...prev, newCat]);
        }}
        onCategoryDeleted={(categoryId) => {
          setCategories((prev) => prev.filter(c => c._id !== categoryId));
        }}
      />

      <ActionItemDialog
        open={showItemDialog}
        onOpenChange={(open) => {
          setShowItemDialog(open);
          if (!open && itemDialogFromHub.current) {
            itemDialogFromHub.current = false;
            window.dispatchEvent(new Event("workspace-return-to-hub"));
          }
        }}
        onSave={handleSaveItem}
        categories={categories}
        item={editingItem}
        onCategoryCreated={(newCat) => {
          setCategories((prev) => [...prev, newCat]);
        }}
      />

      {/* "Need help?" dialogs */}
      <NeedHelpDialog
        open={showNeedHelpDialog}
        onOpenChange={setShowNeedHelpDialog}
        actionItem={helpTargetItem}
        onBreakDown={handleBreakDown}
        onCreateActivity={handleCreateActivity}
      />

      <BreakdownDialog
        open={showBreakdownDialog}
        onOpenChange={setShowBreakdownDialog}
        actionItem={helpTargetItem}
        onSubtasksCreated={handleSubtasksCreated}
      />

      <ActionToActivityDialog
        open={showActivityDialog}
        onOpenChange={setShowActivityDialog}
        actionItem={helpTargetItem}
        onActivityCreated={handleActivityCreated}
      />

      {/* Progress Report Dialog */}
      {progressReportItem && (
        <ProgressReportDialog
          open={showProgressReport}
          onOpenChange={setShowProgressReport}
          takeawayId={progressReportItem._id}
          takeawayContent={progressReportItem.content}
        />
      )}

      {/* AI Extractor Dialog */}
      <UnifiedExtractorDialog
        open={showAIExtractor}
        onOpenChange={setShowAIExtractor}
        onExtractComplete={handleAIExtractComplete}
        categories={categories}
        mode="both"
      />

      {/* Mobile Filter Sheet */}
      <Sheet open={showFilterSheet} onOpenChange={setShowFilterSheet}>
        <SheetContent side={isRTL ? "left" : "right"} className="w-[85vw] sm:w-[400px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              {t("action_items.filters_and_view", "Filters & View")}
            </SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* View Mode */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("action_items.view_mode", "View Mode")}</label>
              <div className="flex rounded-lg bg-muted p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`flex-1 h-9 ${viewMode === "table" ? "bg-background shadow-sm" : ""}`}
                  onClick={() => setViewMode("table")}
                >
                  <LayoutList className="h-4 w-4 mr-2" />
                  {t("action_items.table", "Table")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`flex-1 h-9 ${viewMode === "card" ? "bg-background shadow-sm" : ""}`}
                  onClick={() => setViewMode("card")}
                >
                  <LayoutGrid className="h-4 w-4 mr-2" />
                  {t("action_items.card", "Card")}
                </Button>
              </div>
            </div>

            {/* Group By */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("action_items.group_by", "Group by")}</label>
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupBy)}>
                <SelectTrigger className="w-full">
                  <Group className="h-4 w-4 mr-2 text-muted-foreground" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("action_items.no_grouping", "No grouping")}</SelectItem>
                  <SelectItem value="status">{t("action_items.group_by_status", "By status")}</SelectItem>
                  <SelectItem value="category">{t("action_items.group_by_category", "By category")}</SelectItem>
                  <SelectItem value="priority">{t("action_items.group_by_priority", "By priority")}</SelectItem>
                  <SelectItem value="dueDate">{t("action_items.group_by_due_date", "By due date")}</SelectItem>
                  <SelectItem value="project">{t("action_items.group_by_project", "By project")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("status")}</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_statuses")}</SelectItem>
                  <SelectItem value="pending">{t("pending")}</SelectItem>
                  <SelectItem value="completed">{t("completed")}</SelectItem>
                  <SelectItem value="archived">{t("archived")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Priority Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("priority")}</label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all_priorities")}</SelectItem>
                  <SelectItem value="high">{t("high")}</SelectItem>
                  <SelectItem value="medium">{t("medium")}</SelectItem>
                  <SelectItem value="low">{t("low")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Category Filter */}
            <div className="space-y-3">
              <label className="text-sm font-medium">{t("category")}</label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("action_items.all_categories", "All categories")}</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat._id} value={cat._id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Manage Categories Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowFilterSheet(false);
                setShowCategoryDialog(true);
              }}
            >
              <Settings className="h-4 w-4 mr-2" />
              {t("manage_categories")}
            </Button>

            {/* Item Count */}
            <div className="pt-4 border-t">
              <div className="text-sm text-center">
                <span className="font-medium">{filteredItems.length}</span>
                <span className="text-muted-foreground ml-1">{t("action_items.items", "items")}</span>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ActionItems;
